import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Check,
  User,
  Mail,
  Lock,
  Calendar,
  Sparkles,
  UserCircle,
  RefreshCw,
  ShieldCheck,
  HelpCircle
} from "lucide-react";
import logoLight from "../assets/antaryatra-logo-horizontal 3.0.svg";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import tracker from "../utils/sanctuary-tracker";
import { useLanguage } from "../context/LanguageContext";

// Helper: Calculate age from DOB
function calculateAge(dob) {
  if (!dob) return 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Helper: Random Username Generator
const generateRandomUsername = () => {
  const prefixes = ["Soul", "Mind", "Inner", "Cosmic", "Silent", "Peace", "Zen", "Mystic"];
  const suffixes = ["Traveler", "Seeker", "Walker", "Guide", "Spirit", "Light", "Shadow", "Voice"];
  const num = Math.floor(Math.random() * 9999);
  return `${prefixes[Math.floor(Math.random() * prefixes.length)]}_${suffixes[Math.floor(Math.random() * suffixes.length)]}_${num}`;
};

export default function SignupOnboarding() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const energyState = location.state?.energyState;

  // Left panel quotes per step
  const PANEL_QUOTES = [
    {
      label: t('ob_step1_title'),
      headline: t('ob_step1_head'),
      body: t('ob_step1_body'),
      quote: "\"The journey of a thousand miles begins with a single step.\"",
      author: "— Lao Tzu",
    },
    {
      label: t('ob_step2_title'),
      headline: t('ob_step2_head'),
      body: t('ob_step2_body'),
      quote: "\"Know thyself. Act from within.\"",
      author: "— Antaryatra",
    },
  ];

  const energyMap = {
    turbulent: {
      label: t('sec4_state2'), // Overwhelmed
      headline: t('ob_storm_head'),
      body: t('ob_storm_body'),
    },
    still: {
      label: t('sec4_state4'), // Seeking clarity
      headline: t('ob_still_head'),
      body: t('ob_still_body'),
    },
    inert: {
      label: t('sec4_state3'), // Tired of being strong
      headline: t('ob_inert_head'),
      body: t('ob_inert_body'),
    }
  };

  // 🔍 Check for existing user
  const { user, login, checkSession } = useAuth();
  const isExistingUser = Boolean(user);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState("");

  // Breathing welcome screen state (skip if existing user)
  const [showBreath, setShowBreath] = useState(!isExistingUser);

  // Invitation Awareness (Check URL immediately to prevent skipping steps)
  const params = new URLSearchParams(window.location.search);
  const tokenParam = params.get("token");

  // Steps: 0 = Account (new only), 1 = Details.
  // CRITICAL: If they have a token, they MUST start at Step 0 to set a password, 
  // even if they have a stale session or guest user.
  const [step, setStep] = useState((isExistingUser && !tokenParam) ? 1 : 0);

  // Invitation Awareness state
  const [inviteVerified, setInviteVerified] = useState(false);

  const resetSession = async () => {
    window.location.reload();
  };

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    dob: user?.dob ? new Date(user.dob).toISOString().split("T")[0] : "",
    role: user?.role || "SEEKER",
    username: "",
    gender: "",
    guardianConsent: false,
    isCertified: false,
    captchaAnswer: "",
    website: "", // 🍯 Honeypot field (hidden from humans)
    legalConsent: false,
  });

  // Username sparkle state
  const [showSparkle, setShowSparkle] = useState(false);
  const sparkleTimeout = useRef(null);

  // Auto-fill random username on mount & Check URL for Role & Fetch Details
  useEffect(() => {
    if (!form.username) {
      setForm(p => ({ ...p, username: generateRandomUsername() }));
    }

    // Check URL Params for Role override & Token (e.g. ?role=PROVIDER&token=...)
    const params = new URLSearchParams(window.location.search);
    const roleParam = params.get("role");
    const tokenParam = params.get("token");
    if (roleParam) {
      setForm(p => ({ ...p, role: roleParam.toUpperCase() }));
    }
    if (tokenParam) {
      setForm(p => ({ ...p, token: tokenParam }));
      
      // 🕵️ SMART INVITATION VERIFICATION
      api.get(`/auth/verify-invite?token=${tokenParam}`)
        .then(({ data }) => {
           console.log("Welcome, Reflection recognized:", data.name);
           setForm(p => ({ 
             ...p, 
             name: data.name || p.name, 
             email: data.email || p.email,
             role: "PROVIDER"
           }));
           setInviteVerified(true);
           setShowBreath(false); // Skip intro breathing for approved guides
        })
        .catch(err => {
           console.warn("Invalid reflection token.", err.message);
        });
    }

    // FETCH LATEST USER DATA (Include DOB)
    if (isExistingUser) {
      // Existing user logic
    }

    // 🧘 AYI: Track Visit
    tracker.trackOnboarding(step, step === 0 ? "Account" : "Details");
  }, [isExistingUser, step]);

  // 🔄 SMART SYNC: Ensure form stays in sync with latest user data (for existing users)
  useEffect(() => {
    if (user) {
      setForm(p => ({
        ...p,
        name: p.name || user.name || "",
        email: p.email || user.email || "",
        role: p.role || user.role || "SEEKER",
        dob: p.dob || (user.dob ? new Date(user.dob).toISOString().split("T")[0] : ""),
      }));
    }
  }, [user]);

  const regenerateUsername = () => {
    setForm(p => ({ ...p, username: generateRandomUsername() }));
    // Trigger sparkle
    setShowSparkle(true);
    if (sparkleTimeout.current) clearTimeout(sparkleTimeout.current);
    sparkleTimeout.current = setTimeout(() => setShowSparkle(false), 2400);
  };

  // Simple Math Captcha Logic
  const [captcha, setCaptcha] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, result: a + b };
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  /* ================= NAVIGATION ================= */
  const nextStep = async () => {
    setError(null);

    // 🍯 HONEYPOT CHECK: If the 'website' field is filled, it's a bot.
    if (form.website) {
      console.warn("[SECURITY] Honeypot triggered. Bot detected.");
      // Silent failure: we show a generic server error to not tip off the bot developer
      setError("Unable to process request. Please refresh and try again.");
      return;
    }

    if (step === 0) {
      if (!form.name || !form.email || !form.password || !form.dob) {
        setError("Please fill in all private account details.");
        return;
      }

      const userAge = calculateAge(form.dob);
      if (userAge < 18) {
        setError("You must be 18 or older to join Antaryatra.");
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setEmailError("That email doesn't look quite right.");
        setError("Please enter a valid email address.");
        return;
      }
      setEmailError("");

      const hasUppercase = /[A-Z]/.test(form.password);
      const hasNumber = /[0-9]/.test(form.password);
      const isLongEnough = form.password.length >= 8;

      if (!hasUppercase || !hasNumber || !isLongEnough) {
        setError("Please fix the password errors before continuing.");
        return;
      }

      if (!isExistingUser) {
        if (parseInt(form.captchaAnswer) !== captcha.result) {
          setError("Incorrect math answer. Please prove you are human.");
          return;
        }

        try {
          setLoading(true);
          await api.post("/auth/check-email", { email: form.email });

          const { data } = await api.post("/auth/signup", {
            name: form.name,
            email: form.email,
            password: form.password,
            dob: form.dob,
            role: form.role || "SEEKER",
            token: form.token || null,
          });

          if (data.user) {
            login(data);

            try {
              await api.post("/auth/send-verification", { origin: window.location.origin });
            } catch (e) {
              console.warn("Failed to trigger background verification email", e);
            }

            navigate("/verify-pending", { replace: true });
            return;
          }
        } catch (err) {
          let msg = "Something went wrong. Please try again.";
          if (err.response) {
            msg = err.response.data?.error || "We couldn't create your account.";
          }
          setError(msg);
          tracker.track('onboarding_error', { step: 0, message: msg });
          setLoading(false);
          return;
        } finally {
          setLoading(false);
        }
      }
    }

    setStep((s) => s + 1);
  };

  const prevStep = () => setStep((s) => s - 1);

  /* ================= API ACTIONS ================= */
  const signupIfNeeded = async () => {
    if (isExistingUser) return;

    if (form.website) {
       throw new Error("Security validation failed.");
    }

    if (!form.name || !form.email || !form.password || !form.role) {
      throw new Error("Missing signup details");
    }

    if (!form.dob) {
      throw new Error("Date of birth is required for signup.");
    }

    const userAge = calculateAge(form.dob);
    if (userAge < 18) {
      throw new Error("We apologize, but you must be 18 or older to join Antaryatra.");
    }

    const { data } = await api.post("/auth/signup", {
      name: form.name,
      email: form.email,
      password: form.password,
      dob: form.dob,
      role: form.role,
      token: form.token || null,
      website: form.website || "", // Pass it to backend for double-check
    });

    if (data.user) {
      login(data);
    }

    update("role", data.user.role);
  };

  const completeOnboarding = async () => {
    if (!form.legalConsent) {
      throw new Error("Please agree to the Terms and Privacy Policy to continue.");
    }

    if (form.role === "SEEKER") {
      const derivedAge = calculateAge(form.dob);
      if (!form.username) throw new Error("Please complete your seeker profile.");

      await api.post("/onboarding/seeker", {
        username: form.username,
        age: derivedAge,
        guardianConsent: false
      });
    }

    if (form.role === "PROVIDER") {
      if (!form.username || !form.gender) throw new Error("Please complete your provider profile.");

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("dob", form.dob);
      formData.append("gender", form.gender);
      formData.append("username", form.username);
      if (form.aadhaar) formData.append("aadhaar", form.aadhaar);

      await api.post("/onboarding/provider", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }
  };

  const submit = async () => {
    try {
      setLoading(true);
      setError(null);
      await signupIfNeeded();
      await completeOnboarding();
      await checkSession();
      
      // 🧘 AYI/GA4: Track Completion
      tracker.trackOnboarding(2, "Complete");
      tracker.track('onboarding_complete', { role: form.role });
      
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Signup Error:", err);
      let msg = err.message || "An unexpected error occurred.";

      if (err.response?.data) {
        if (err.response.data.error) {
          msg = err.response.data.error;
        } else {
          msg = `Server Error (${err.response.status}): ${typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)}`;
        }
      }
      setError(msg);
      tracker.track('onboarding_error', { step: 1, message: msg });
    } finally {
      setLoading(false);
    }
  };

  /* ================= BREATH SCREEN ================= */
  if (showBreath) {
    return <BreathScreen onDone={() => setShowBreath(false)} />;
  }

  /* ================= MAIN UI ================= */
  let panelData = PANEL_QUOTES[Math.min(step, PANEL_QUOTES.length - 1)];

  // Inject Personalization if energy state exists and we are on Step 0 (Account)
  if (step === 0 && energyState && energyMap[energyState]) {
    panelData = {
      ...panelData,
      ...energyMap[energyState]
    };
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-serenity-blue-50 to-serenity-purple-50 dark:from-gray-900 dark:to-gray-800">

      {/* LEFT PANEL: Cosmos (Hidden on Mobile) */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden bg-brand-gradient-dark">

        {/* Outer Glows/Stars for Desktop ONLY to save CPU on Mobile */}
        <div className="absolute inset-0 pointer-events-none opacity-50" style={{
          background: "radial-gradient(ellipse 60% 50% at 50% 55%, rgba(99,102,241,0.18) 0%, transparent 70%)",
        }} />
        
        {/* Top-left brand mark */}
        <div className="relative z-10">
          <img src={logoLight} alt="Antaryatra" className="h-10 w-auto object-contain" />
        </div>

        {/* Centre hero copy — animates on step change */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`panel-${step}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative z-10 flex flex-col justify-center flex-1 py-12"
          >
            <p className="text-indigo-300 text-xs font-semibold tracking-[0.25em] uppercase mb-6">
              {panelData.label}
            </p>
            <h1 className="text-5xl font-display font-bold text-white leading-[1.15] mb-4">
              {panelData.headline}
            </h1>
            <p className="text-indigo-200/70 text-lg font-light leading-relaxed max-w-xs">
              {panelData.body}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Quote at bottom */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`quote-${step}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative z-10 border-l-2 border-indigo-400/50 pl-4"
          >
            <p className="text-indigo-100/80 text-sm leading-relaxed italic">
              {panelData.quote}
            </p>
            <p className="mt-2 text-indigo-300/60 text-xs font-semibold tracking-wide">{panelData.author}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* RIGHT PANEL: Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="w-full max-w-md space-y-8 z-10">

          {/* MOBILE ONLY: Personalized Intro (Since left panel is hidden) */}
          <div className="lg:hidden p-5 sm:p-6 rounded-2xl shadow-lg border mb-6 transition-colors duration-300
            bg-white dark:bg-brand-gradient-dark 
            border-indigo-100 dark:border-indigo-500/20 
            shadow-indigo-500/5 dark:shadow-none"
          >
            <div className="flex justify-between items-start mb-3">
              <img src={logoLight} alt="Antaryatra" className="h-5 w-auto object-contain dark:opacity-100 opacity-80 filter dark:invert-0 invert" />
              <p className="text-indigo-500 dark:text-indigo-300 text-[9px] font-semibold tracking-[0.2em] uppercase">{panelData.label}</p>
            </div>
            <h1 className="text-xl sm:text-2xl font-display font-bold text-gray-900 dark:text-white mb-2 leading-tight">{panelData.headline}</h1>
            <p className="text-gray-600 dark:text-indigo-200/80 text-xs sm:text-sm leading-relaxed mb-4">{panelData.body}</p>
            <div className="border-l-2 border-indigo-300 dark:border-indigo-400/50 pl-3">
              <p className="text-gray-500 dark:text-indigo-100/70 text-[11px] sm:text-xs italic leading-snug">"{panelData.quote}"</p>
            </div>
          </div>

          {/* Header with Soulful Progress Trail */}
          <div className="space-y-2">
            {/* Progress Trail */}
            <SoulfulProgress step={step} />

            <motion.h2
              key={`h2-${step}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-3"
            >
              {step === 0 ? t('ob_safe_space_title') : t('ob_step2_head')}
            </motion.h2>
            <motion.p
              key={`subhead-${step}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-lg text-gray-600 dark:text-gray-300"
            >
              {step === 0 ? t('ob_safe_space_sub') : t('ob_step2_body')}
            </motion.p>
            {isExistingUser && (
              <button onClick={() => { localStorage.clear(); window.location.reload(); }} className="text-xs text-brand-blue underline mt-1">
                Not {user?.name}? Start over
              </button>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100 flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <span className="block w-2 h-2 rounded-full bg-red-500 shrink-0" />
                {error}
              </div>
              {(error.includes("Authentication") || error.includes("User record missing")) && (
                <button onClick={resetSession} className="text-xs text-red-700 font-semibold underline self-start ml-4">
                  Reset Session & Start Over
                </button>
              )}
            </motion.div>
          )}

          {/* Form Content */}
          <div className="min-h-[300px]">
            <AnimatePresence mode="wait">

              {/* STEP 0: Account */}
              {step === 0 && (
                <motion.div
                  key="step0"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="space-y-4">
                    <InputIcon
                      icon={Lock}
                      placeholder={t('ob_real_name_label')}
                      value={form.name}
                      onChange={e => update("name", e.target.value)}
                      privateField
                      subtext={t('ob_real_name_sub')}
                    />
                    <div>
                      <InputIcon
                        icon={Mail}
                        placeholder={t('ob_email_label')}
                        value={form.email}
                        onChange={e => {
                          update("email", e.target.value);
                          if (emailError) setEmailError("");
                        }}
                        onBlur={() => {
                          const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (form.email && !regex.test(form.email)) {
                            setEmailError(t('ob_email_error'));
                          } else {
                            setEmailError("");
                          }
                        }}
                        privateField
                        className={emailError ? "border-red-300 focus:ring-red-200 focus:border-red-500" : ""}
                      />
                      {emailError && <p className="text-xs text-red-500 mt-1 pl-1">{emailError}</p>}
                    </div>

                    <DOBPicker
                      value={form.dob}
                      onChange={val => update("dob", val)}
                    />

                    <InputIcon
                      icon={Lock}
                      type="password"
                      placeholder={t('ob_pass_label')}
                      value={form.password}
                      onChange={e => update("password", e.target.value)}
                      privateField
                    />
                    {/* Password Strength Checklist */}
                    <div className="text-xs space-y-1 ml-1 pt-1">
                      <div className={`flex items-center gap-2 ${form.password.length >= 8 ? "text-green-600 font-medium" : "text-gray-400"}`}>
                        {form.password.length >= 8 ? <Check size={12} /> : <div className="w-3 h-3 rounded-full border border-gray-300" />}
                        {t('ob_pass_len')}
                      </div>
                      <div className={`flex items-center gap-2 ${/[A-Z]/.test(form.password) ? "text-green-600 font-medium" : "text-gray-400"}`}>
                        {/[A-Z]/.test(form.password) ? <Check size={12} /> : <div className="w-3 h-3 rounded-full border border-gray-300" />}
                        {t('ob_pass_upper')}
                      </div>
                      <div className={`flex items-center gap-2 ${/[0-9]/.test(form.password) ? "text-green-600 font-medium" : "text-gray-400"}`}>
                        {/[0-9]/.test(form.password) ? <Check size={12} /> : <div className="w-3 h-3 rounded-full border border-gray-300" />}
                        {t('ob_pass_num')}
                      </div>
                      
                      {/* 🍯 Honeypot Input (Invisible to Humans) */}
                      <div className="opacity-0 absolute -top-[9999px] -left-[9999px] pointer-events-none" aria-hidden="true">
                        <input
                          type="text"
                          name="website"
                          tabIndex="-1"
                          autoComplete="off"
                          value={form.website}
                          onChange={e => update("website", e.target.value)}
                          placeholder="Your website URL"
                        />
                      </div>
                    </div>

                    {/* Math Captcha */}
                    <div className="pt-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('ob_captcha_label', { a: captcha.a, b: captcha.b })}
                      </label>
                      <InputIcon
                        icon={Sparkles}
                        placeholder={t('ob_captcha_placeholder')}
                        type="number"
                        value={form.captchaAnswer}
                        onChange={e => update("captchaAnswer", e.target.value)}
                      />
                    </div>
                  </div>

                  <button onClick={nextStep} disabled={loading} className="btn-primary w-full mt-8 group disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center h-[52px]">
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        {t('ob_cta_begin')} <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-sm text-gray-500 mt-4">
                    {t('ob_login_prompt')} <span className="text-brand-blue font-semibold cursor-pointer" onClick={() => navigate("/login")}>{t('ob_login_link')}</span>
                  </p>

                  <p className="text-center text-xs text-gray-400 mt-6 italic opacity-70">
                    {t('ob_nudge_bottom')}
                  </p>
                </motion.div>
              )}

              {/* STEP 1: Details */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >

                  {/* Public Username Generator */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('ob_anon_name_label')}
                      </label>
                      <button
                        type="button"
                        onClick={regenerateUsername}
                        className="text-xs text-brand-blue flex items-center gap-1 hover:underline"
                      >
                        <RefreshCw size={12} /> {t('ob_anon_name_gen')}
                      </button>
                    </div>

                    {/* Username field with sparkle overlay */}
                    <div className="relative">
                      <InputIcon
                        icon={UserCircle}
                        placeholder="e.g. Soul_Traveler_123"
                        value={form.username}
                        onChange={e => update("username", e.target.value)}
                        subtext={showSparkle ? t('ob_anon_name_magic') : t('ob_anon_name_sub')}
                      />

                      {/* Sparkle burst particles */}
                      <AnimatePresence>
                        {showSparkle && (
                          <motion.div
                            key="sparkles"
                            className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            {[...Array(8)].map((_, i) => (
                              <motion.span
                                key={i}
                                className="absolute text-indigo-400 font-bold select-none"
                                style={{
                                  left: `${15 + (i * 10)}%`,
                                  top: "50%",
                                  fontSize: `${10 + (i % 3) * 4}px`,
                                }}
                                initial={{ y: 0, opacity: 1, scale: 0.5 }}
                                animate={{
                                  y: -28 - (i % 4) * 8,
                                  opacity: 0,
                                  scale: 1.4,
                                  x: (i % 2 === 0 ? -1 : 1) * (i * 4),
                                }}
                                transition={{ duration: 0.8, delay: i * 0.06, ease: "easeOut" }}
                              >
                                ✦
                              </motion.span>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* AGE CHECK WARNING (Dynamic) */}
                  {calculateAge(form.dob) > 0 && calculateAge(form.dob) < 18 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="p-4 bg-amber-50 rounded-xl border border-amber-100 text-amber-800 text-sm flex gap-3"
                    >
                      <ShieldCheck className="w-5 h-5 shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">{t('ob_age_warn_title')}</p>
                        {t('ob_age_warn_desc')}
                        <br /><br />
                        {t('ob_age_crisis')}
                      </div>
                    </motion.div>
                  )}

                  {form.role === "PROVIDER" && (
                    <div className="space-y-4">
                      <select
                        required
                        className="w-full px-4 py-3 pl-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-brand-blue focus:outline-none text-gray-700 dark:text-gray-300"
                        value={form.gender}
                        onChange={e => update("gender", e.target.value)}
                      >
                        <option value="" disabled>Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Non-binary">Non-binary</option>
                        <option value="LGBT+ Family">LGBT+ Family</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </select>

                      <div className="grid grid-cols-1 gap-4">
                        <InputIcon
                          icon={Lock}
                          placeholder={t('ob_aadhaar_label')}
                          value={form.aadhaar || ""}
                          onChange={e => update("aadhaar", e.target.value)}
                          privateField
                          subtext={t('ob_aadhaar_sub')}
                        />
                      </div>
                    {/* Legal Consent Checkbox */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 mt-2">
                      <input
                        type="checkbox"
                        id="legalConsent"
                        checked={form.legalConsent}
                        onChange={(e) => update("legalConsent", e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer"
                      />
                      <label htmlFor="legalConsent" className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed cursor-pointer">
                        I have read and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-medium hover:underline">Terms of Space</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-medium hover:underline">Privacy of Spirit</a>.
                      </label>
                    </div>
                  </div>
                )}

                  <div className="flex gap-3 mt-8">
                    <button onClick={prevStep} className="btn-secondary flex-1 text-sm">
                      {t('ob_back')}
                    </button>
                    <button onClick={submit} disabled={loading} className="btn-primary flex-1 text-sm shadow-xl shadow-indigo-500/20">
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          {t('ob_starting')}
                        </span>
                      ) : t('ob_finish')}
                    </button>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ================= BREATH SCREEN ================= */
function BreathScreen({ onDone }) {
  const [phase, setPhase] = useState("nudge"); // nudge | inhale | hold | exhale | done
  const [fadeOut, setFadeOut] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Sequence: nudge 2.2s → inhale 2s → hold 0.8s → exhale 2s → fade & done
    const t0 = setTimeout(() => setPhase("inhale"), 2200);
    const t1 = setTimeout(() => setPhase("hold"), 4200);
    const t2 = setTimeout(() => setPhase("exhale"), 5000);
    const t3 = setTimeout(() => setFadeOut(true), 7000);
    const t4 = setTimeout(() => onDone(), 7600);
    return () => [t0, t1, t2, t3, t4].forEach(clearTimeout);
  }, [onDone]);

  const phaseText = {
    nudge: t('ob_nudge'),
    inhale: t('ob_inhale'),
    hold: t('ob_hold'),
    exhale: t('ob_exhale'),
    done: "",
  };

  const circleScale = {
    nudge: 0.5,
    inhale: 1.35,
    hold: 1.35,
    exhale: 0.85,
    done: 0.85,
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ background: "linear-gradient(135deg, #0a0a1a 0%, #0f0c29 50%, #1a133a 100%)" }}
      animate={{ opacity: fadeOut ? 0 : 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Stars (subtle) */}
      {[[10, 15], [80, 25], [30, 70], [65, 10], [50, 85], [20, 50], [90, 60]].map(([x, y], i) => (
        <div key={i} className="absolute rounded-full bg-white/60" style={{
          left: `${x}%`, top: `${y}%`, width: "2px", height: "2px",
          animation: `starPulse ${2 + i * 0.4}s ease-in-out infinite alternate`,
        }} />
      ))}

      {/* Breathing circle */}
      <motion.div
        className="relative flex items-center justify-center"
        animate={{ scale: circleScale[phase] }}
        transition={{ duration: phase === "hold" ? 0.4 : 2, ease: phase === "hold" ? "easeInOut" : "easeInOut" }}
      >
        {/* Outer glow ring */}
        <div className="absolute rounded-full"
          style={{
            width: 180, height: 180,
            background: "radial-gradient(circle, rgba(128,90,213,0.22) 0%, transparent 70%)",
            animation: "breathGlow 4s ease-in-out infinite",
          }}
        />
        {/* Main circle */}
        <div className="w-28 h-28 rounded-full flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, rgba(99,102,241,0.5) 0%, rgba(128,90,213,0.6) 100%)",
            boxShadow: "0 0 40px rgba(128,90,213,0.4), inset 0 0 20px rgba(255,255,255,0.05)",
            border: "1px solid rgba(165,180,252,0.25)",
          }}
        >
          <span className="text-3xl">🌸</span>
        </div>
      </motion.div>

      {/* Phase text */}
      <AnimatePresence mode="wait">
        <motion.p
          key={phase}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4 }}
          className="mt-10 text-indigo-200/90 text-xl font-light tracking-wide"
        >
          {phaseText[phase]}
        </motion.p>
      </AnimatePresence>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.55 }}
        transition={{ delay: 0.8, duration: 1 }}
        className="mt-4 text-indigo-300/50 text-sm font-light"
      >
        You've taken the first step. You're in a safe space.
      </motion.p>

      <style>{`
        @keyframes starPulse {
          from { opacity: 0.3; transform: scale(0.9); }
          to   { opacity: 1;   transform: scale(1.1); }
        }
        @keyframes breathGlow {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50%       { transform: scale(1.15); opacity: 1; }
        }
      `}</style>
    </motion.div>
  );
}

/* ================= SOULFUL PROGRESS TRAIL ================= */
function SoulfulProgress({ step }) {
  const steps = [
    { label: "Planting your seed" },
    { label: "Giving it a name" },
  ];

  return (
    <div className="flex items-center gap-3 mb-4">
      {steps.map((s, i) => {
        const isActive = i === step;
        const isDone = i < step;
        return (
          <div key={i} className="flex items-center gap-2">
            {/* Dot */}
            <div className="relative flex items-center justify-center">
              <motion.div
                className="rounded-full"
                animate={{
                  width: isActive ? 12 : 8,
                  height: isActive ? 12 : 8,
                  backgroundColor: isDone
                    ? "#6366f1"
                    : isActive
                      ? "#818cf8"
                      : "#d1d5db",
                }}
                transition={{ duration: 0.35 }}
              />
              {isActive && (
                <motion.div
                  className="absolute rounded-full"
                  style={{ background: "rgba(129,140,248,0.3)" }}
                  animate={{ width: [12, 22, 12], height: [12, 22, 12], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                />
              )}
              {isDone && (
                <Check size={7} className="absolute text-white" strokeWidth={3} />
              )}
            </div>

            {/* Label (only active) */}
            <AnimatePresence>
              {isActive && (
                <motion.span
                  key="label"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  className="text-xs font-semibold text-indigo-500 dark:text-indigo-400 tracking-wide"
                >
                  {s.label}
                </motion.span>
              )}
            </AnimatePresence>

            {/* Connector line */}
            {i < steps.length - 1 && (
              <div className="w-8 h-px bg-gray-200 dark:bg-gray-700 mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ================= DOB PICKER ================= */
function DOBPicker({ value, onChange }) {
  const [localY, setLocalY] = useState("");
  const [localM, setLocalM] = useState("");
  const [localD, setLocalD] = useState("");

  useEffect(() => {
    // Only sync from upstream value if our local state is empty and a valid date string is passed.
    if (value && value.includes("-") && !localY && !localM && !localD) {
      const parts = value.split("-");
      setLocalY(parts[0]);
      setLocalM(String(parseInt(parts[1], 10)));
      setLocalD(String(parseInt(parts[2], 10)));
    }
  }, [value, localY, localM, localD]);

  const emit = (y, m, d) => {
    if (y && m && d) {
      onChange(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    } else {
      onChange("");
    }
  };

  const handleDay = (e) => {
    const val = e.target.value;
    setLocalD(val);
    emit(localY, localM, val);
  };

  const handleMonth = (e) => {
    const val = e.target.value;
    setLocalM(val);
    emit(localY, val, localD);
  };

  const handleYear = (e) => {
    const val = e.target.value;
    setLocalY(val);
    emit(val, localM, localD);
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentYear = new Date().getFullYear();
  // Dynamic: e.g. in 2027 someone born in 2009 turns 18 and becomes eligible
  const years = Array.from({ length: 82 }, (_, i) => currentYear - 18 - i);

  const daysInMonth = localY && localM
    ? new Date(Number(localY), Number(localM), 0).getDate()
    : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const selectClass =
    "w-full px-3 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl " +
    "focus:ring-2 focus:ring-brand-blue focus:border-brand-blue focus:outline-none transition-all " +
    "text-gray-700 dark:text-gray-200 text-sm cursor-pointer";

  return (
    <div className="space-y-1">
      {/* Header row: label + Private badge — matches InputIcon pattern */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 pl-1">
          <Calendar className="w-4 h-4 text-gray-400" /> Date of Birth
        </label>
        <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-100 flex items-center gap-1 font-medium">
          <Lock size={8} /> Private
        </span>
      </div>

      {/* Three selects */}
      <div className="grid grid-cols-3 gap-2">
        <select
          className={selectClass}
          value={localD}
          onChange={handleDay}
        >
          <option value="">Day</option>
          {days.map(d => (
            <option key={d} value={String(d)}>{d}</option>
          ))}
        </select>

        <select
          className={selectClass}
          value={localM}
          onChange={handleMonth}
        >
          <option value="">Month</option>
          {months.map((m, i) => (
            <option key={m} value={String(i + 1)}>{m}</option>
          ))}
        </select>

        <select
          className={selectClass}
          value={localY}
          onChange={handleYear}
        >
          <option value="">Year</option>
          {years.map(y => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </div>

      <p className="text-[11px] text-gray-400 ml-1 flex items-center gap-1">
        <Lock size={10} className="text-green-500" /> Only to keep this space safe for everyone.
      </p>
    </div>
  );
}


/* --- Helpers --- */
function InputIcon({ icon: Icon, className, subtext, privateField, ...props }) {
  return (
    <div className="space-y-1">
      {privateField && (
        <div className="flex justify-end">
          <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-100 flex items-center gap-1 font-medium">
            <Lock size={8} /> Private
          </span>
        </div>
      )}

      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-blue transition-colors flex items-center gap-2 pointer-events-none z-10">
          <Icon className="w-5 h-5" />
        </div>
        <input
          className={`w-full px-4 py-3 pl-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue focus:outline-none transition-all placeholder:text-gray-400 ${className}`}
          {...props}
        />
      </div>
      {subtext && (
        <motion.p
          key={subtext}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="text-[11px] text-gray-400 ml-1 flex items-center gap-1"
        >
          {privateField ? <Lock size={10} className="text-green-500" /> : <HelpCircle size={10} />} {subtext}
        </motion.p>
      )}
    </div>
  );
}

function RoleCard({ selected, onClick, icon: Icon, title, desc }) {
  return (
    <div
      onClick={onClick}
      className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-200 flex items-start gap-4 ${selected
        ? "border-brand-blue bg-blue-50/50 dark:bg-brand-blue/10 ring-1 ring-brand-blue"
        : "border-gray-200 dark:border-gray-700 hover:border-brand-blue/50 hover:bg-gray-50 dark:hover:bg-gray-800"
        }`}
    >
      <div className={`p-3 rounded-xl shrink-0 ${selected ? "bg-brand-blue text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-500"}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h3 className={`font-semibold text-lg ${selected ? "text-brand-blue" : "text-gray-900 dark:text-white"}`}>
          {title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{desc}</p>
      </div>
      {selected && <div className="ml-auto text-brand-blue"><Check className="w-6 h-6" /></div>}
    </div>
  );
}
